/*
* Created on Mar 08, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import React, {useEffect, useState, useRef} from 'react';
import TableComponent from "../common/TableComponent/TableComponent";
import ModalAlertComponent from "../common/ModalComonent/ModalAlertComponent";
import ic_content_contact_svg from '../../static/img/content/ic-content-contacts.svg';
import {useParams, useSearchParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import AccountListTableRowComponent from "./AccountListTableRowComponent";
import AccountListInviteModalComponent from "./AccountListInviteModalComponent";
import AccountListEditAdminModalComponent from "./AccountListEditAdminModalComponent";
import {useAuthSession} from "../../context/AuthSessionContext";
import {useTranslation} from "react-i18next";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useReducerAsync} from "use-reducer-async";
import {useOrganization} from "../../context/OrganizationContext";
import {disableAccountCheckBox} from "../../service/utils";
import useEmailChangedHook from "../../hook/EmailChangedHook";
import AccountListRevokeOrgUserSpaceModal from "./AccountListRevokeOrgUserSpaceModal";
import {useAlertContext} from "../../context/AlertContext";
import UserRolePermissionModal from "./UserRolePermissionModal";
import AccountListSuspendModal from "./AccountListSuspendModal";
import AccountListManageShareAccess from "./AccountListManageShareAccess";


const table_ui = {
    toolbar: {
        count: true,
        create_button: true,
        delete_button: true,
        export_button: false
    },
    table_field_checkbox: true
}

const AccountListIndexComponent = () => {
    const {t, i18n} = useTranslation();
    const currentUser = JSON.parse(window.localStorage.getItem('currentUser'));
    const {userProfile, setCurrentOrg} = useOrganization();
    const {is_owner, permissions, role} = userProfile();
    const canManageAccounts = is_owner || role === 'user_manager';
    const {OrganizationAPI} = useOrganizationApi();
    const {useCheckSession} = useAuthSession();
    const {oid} = useParams();
    const [searchParams] = useSearchParams();
    const [page, setPage] = useState({limit:10, offset:0});
    const [refresh, setRefresh] = useState(1);
    const [openInviteModal, setOpenInviteModal] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [rowData, setRowData] = useState(null);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openLeaveAlert, setOpenLeaveAlert] = useState(false);
    const urlFiltersApplied = useRef(false);
    const {EmailWarningDialog} = useEmailChangedHook();
    const [defaultLeaveWarning, setDefaultLeaveWarning] = useState('@You will no longer have access to any QNAP services or data within this organization. Your Personal Space, if present, will be transferred to the Organization Owner and only visible to them. If you currently pay for the license subscriptions, leaving will not cancel them automatically. Please manually unsubscribe and notify your organization owner to reassign licenses if needed.@');
    const alert = useAlertContext();

    console.log('AccountListIndexComponent render search params', searchParams.get('filter'));

    const { data: org} = useQuery({
        queryKey: [`organization/${oid}`],
        queryFn: async ({signal}) => {
            const org_data = await OrganizationAPI.get(`organization/${oid}`, {}, false, signal);
            setCurrentOrg(org_data.result);
            return org_data.result;
        }
    });

    const { data:org_user, isSuccess } = useQuery({
        queryKey: [`organization/${oid}/user`, page, refresh],
        queryFn: async ({signal}) => {
            const org_user_list = await OrganizationAPI.get(`organization/${oid}/user`, {...page}, false, signal);
            const merged_user_list = new Map();
            org_user_list.result.forEach((user) => merged_user_list.set(user.id, user));
            try {
                const space_org_user = await OrganizationAPI.q2.get(`org_storage/${oid}/user`);
                space_org_user.result.forEach((user) => {
                    if (merged_user_list.has(user.user_id)) {
                        user.has_seat = user?.seated_regions && Object.keys(user.seated_regions).length > 0 ? 'seated' : 'unseated';
                        merged_user_list.set(user.user_id, {...merged_user_list.get(user.user_id), ...user});
                    }
                });
            } catch (e) {
            }
            return Array.from(merged_user_list.values());
        },
        enabled: !!org
    });

    const { data: total_users} = useQuery({
        queryKey: [`organization/${oid}/user/count`, refresh],
        queryFn: async () => {
            const org_user_list = await OrganizationAPI.get(`organization/${oid}/user/count`);
            return org_user_list.result;
        },
        enabled: !!org
    });

    const {data:devices} = useQuery({
        queryKey: ['/device', oid, page, refresh],
        queryFn: async ({signal}) => {
            const res = await OrganizationAPI.device.get('', {organization_id: oid, limit: 200}, signal);
            if (res?.result) {
                return res.result.map((device) => {
                    return {
                        id: device?.device_id,
                        name: device?.device_name,
                        organization_site_id: device?.organization_site_id
                    }
                });
            }
            return res?.result;
        },
        enabled: !!oid
    });

    const {data:org_sites, isFetched: is_org_site_fetched} = useQuery({
        queryKey: [`organization/${oid}/site`, page, refresh, i18n.language],
        queryFn: async ({signal}) => {
            const res= await OrganizationAPI.get(`organization/${oid}/site`, {...page, gt: true, lang: i18n.language}, false, signal);
            let site_devices = {};
            devices?.forEach((device) => {
                if (device?.organization_site_id) {
                    if (!site_devices[device.organization_site_id]) {
                        site_devices[device.organization_site_id] = [];
                    }
                    site_devices[device.organization_site_id].push(device);
                }
            });
            console.log('site_devices', site_devices);
            if (res?.result?.sites) {
                return res.result.sites.map((site) => {
                    return {
                        id: site?.organization_site_id,
                        name: site?.display_name || site?.name,
                        devices: site_devices[site?.organization_site_id] || [],
                    }
                });
            }
            return res?.result?.sites;
        },
        enabled: !!devices
    });

    const table_head_fields = [
        {field_name: t('@Account Name@'), isSortable: true, width_style: '!w-20', field_id: 'display_name', default_sort: true, default_sort_order: ''},
        {field_name: t('@Status@'), isSortable: false, width_style: '!w-12', field_id: 'status', filters: [{name: t('@Active@'), value: 'active'}, {name: t('@Invited@'), value: 'pending'}, {name: t('@Suspended@'), value: 'suspend'}, {name: t('@QNAP ID not activate@'), value: 'qid_not_activated'}]},
        {field_name: t('@Role@'), isSortable: false, width_style: '!w-10'},
        {field_name: t('@Total Usage@'), isSortable: false, width_style: '!w-18', field_id: 'has_seat', info: {show_info: true, info_text: t('@Total storage used by this user across all regions, including files in Personal Space@')}, filters: [{name: t('@Assigned@'), value: 'seated'}, {name: t('@Unassigned@'), value: 'unseated'}]},
        {field_name: t('@Permissions@'), isSortable: false, width_style: '!w-22'},
        {field_name: t('@Date@'), isSortable: false, width_style: '!w-13'},
        {field_name: t('@Action@'), isSortable: false, width_style: '!w-120px'},
    ];

    const closeInviteModal = () => setOpenInviteModal(false);
    const closeEditModal = () => setOpenEditModal(false);

    const onCloseLeaveAlert = (close) => {
        setDefaultLeaveWarning('@You will no longer have access to any QNAP services or data within this organization. Your Personal Space, if present, will be transferred to the Organization Owner and only visible to them. If you currently pay for the license subscriptions, leaving will not cancel them automatically. Please manually unsubscribe and notify your organization owner to reassign licenses if needed.@');
        setOpenLeaveAlert(false);
    }

    const leaveOrganization = async (alert, setSimpleButton) => {
        try {
            await OrganizationAPI.delete(`organization/${oid}/user/${currentUser.user_id}`)
            setOpenLeaveAlert(false);
            setRefresh(prev=>prev+1);
        } catch (e) {
            setSimpleButton(true);
            setDefaultLeaveWarning('@Last one to leave organization warning message@')
        }
        // show global message is needed.
    }

    const dispatch_handler = (accountList_state, action) => {
        switch (action.type) {
            case 'init':
                accountList_state.data = action.payload;
                accountList_state.select_box = false;
                accountList_state.search = (obj, query='') => {
                    return obj?.display_name?.match(new RegExp(query, 'i')) ||
                        obj?.email?.match(new RegExp(query, 'i'))  ||
                        obj?.qid?.match(new RegExp(query, 'i')) ||
                        obj?.groups?.filter(g=>g.name.match(new RegExp(query, 'i'))).length > 0 || (obj?.is_owner && t('@Owner@').match(RegExp(query, 'i')));
                }
                accountList_state.doFilter = (obj, state) => {
                    const filters = state?.filters;
                    if (!filters || Object.keys(filters).length === 0) return true;
                    const fields = Object.keys(filters);
                    return fields.every((field_id) => filters[field_id].length === 0 || filters[field_id].includes(obj[field_id]));
                };
                accountList_state.openLeaveAlert = openLeaveAlert;
                accountList_state.showRevoke = false;
                accountList_state.showsuspend = false;
                accountList_state.showShareFolder = false;
                accountList_state.sites = org_sites || [];
                accountList_state.devices = devices || [];
                accountList_state.currentUserRole = role;
                // Preserve existing filters or use initial filters from URL, or empty object
                accountList_state.filters = accountList_state.filters || action.initialFilters || {};
                console.log('accountList_state', accountList_state);
                // accountList_state.query = ''
                return {...accountList_state};
            case 'pagination':
                setPage({limit:action.payload.limit, offset:action.payload.offset});
                if (action.payload.refresh) {
                    setRefresh(prev=>prev+1);
                }
                return {...accountList_state}
            case 'select_all':
                const selected = accountList_state.data.map((g)=>({...g, checked: !disableAccountCheckBox(g, currentUser, is_owner, permissions, role)}))
                return {
                    ...accountList_state,
                    data: selected,
                    select_box: selected?.length > 0
                }
            case 'select_none':
                return {
                    ...accountList_state,
                    data: accountList_state.data.map((g)=>({...g, checked: false})),
                    select_box: false
                }
            case 'toggle_checkbox':
                const d = accountList_state.data.map((o) => {
                    if (action.payload.id === o.id) {
                        return {...o, checked: !o.checked}
                    }
                    return o;
                });
                let check_status = d?.every((o) => o.checked);
                if (!check_status) {
                    check_status = d?.some((o) => o.checked) ? 'partial' : false;
                }
                return {
                    ...accountList_state,
                    data: d,
                    select_box: check_status
                }
            case 'search_account_list':
                return {
                    ...accountList_state,
                    query: action.payload
                };
            case 'create_account_list':
                setOpenInviteModal(true);
                return accountList_state;
            case 'open_alert_delete_account_list':
                setOpenAlert(true);
                // setAlertConfirmAction('delete_admin_account');
                return accountList_state;
            case 'delete_admin_account':
                const qids = accountList_state.data.filter((u)=>u.checked).map((u)=>u.email || u.qid);
                OrganizationAPI.delete(`organization/${oid}/user`, null, {qids})
                    .then(data=>{
                        setRefresh(prevRefresh=>prevRefresh+1);
                        setOpenAlert(false);
                    })
                return accountList_state;
            case 'open_edit_modal':
                console.log(action.payload)
                setRowData(action.payload);
                setOpenEditModal(true);
                return accountList_state;
            case 'open_leave_alert':
                setOpenLeaveAlert(action.payload);
                return accountList_state;
            case 'toggle_show_revoke':
                return {
                    ...accountList_state,
                    showRevoke: action.payload.showRevoke,
                    row_data: action.payload.row_data
                }
            case 'toggle_show_permissions':
                return {
                    ...accountList_state,
                    showPermissions: action.payload.showPermissions,
                    row_data: action.payload.row_data
                }
            case 'toggle_show_suspend':
                return {
                    ...accountList_state,
                    showSuspend: action.payload.showSuspend,
                    row_data: action.payload.row_data
                }
            case 'toggle_show_share':
                return {
                    ...accountList_state,
                    showShareFolder: action.payload.showShareFolder,
                    row_data: action.payload.row_data
                }
            case 'toggle_filter':
                return {
                    ...accountList_state,
                    filters: {
                        ...accountList_state.filters,
                        [action.payload.field_id]: action.payload.filters
                    },
                }
            default:
                return accountList_state;
        }
    }

    const asyncDispatchHandler = {
        deleteAdminAccount: ({dispatch, getState}) => async (action) => {
            const accountList_state = getState();
            const qids = accountList_state.data.filter((u)=>u.checked).map((u)=>u.email || u.qid);
            try {
                await OrganizationAPI.delete(`organization/${oid}/user`, null, {qids})
                const curOrg = JSON.parse(localStorage.getItem('currentOrg'));
                curOrg.users = curOrg.users.filter(u => !qids.includes(u.email));
                localStorage.setItem('currentOrg', JSON.stringify(curOrg));
                setRefresh(prevRefresh=>prevRefresh+1);
                setOpenAlert(false);
                alert(true, 'success', 'ok',  t('@Accounts removed from the organization.@'), 10*1000);
            } catch (err) {
                action.alert(true, 'error', err.response.data.code, err.response.data.message);
                throw err;
            }
            // return accountList_state;
        }
    }

    // const [state, dispatch] = useReducer(dispatch_handler, {});
    const [state, dispatch] = useReducerAsync(dispatch_handler, {}, asyncDispatchHandler);

    useCheckSession();
    useEffect(() => {
        if (org_user) {
            // Parse URL filter parameter (supports multiple comma-separated values)
            let initialFilters = null;
            const filterParam = searchParams.get('filter');
            if (filterParam && !urlFiltersApplied.current) {
                urlFiltersApplied.current = true;
                const filterMapping = {
                    'assigned': { field_id: 'has_seat', value: 'seated' },
                    'unassigned': { field_id: 'has_seat', value: 'unseated' },
                    'active': { field_id: 'status', value: 'active' },
                    'pending': { field_id: 'status', value: 'pending' },
                    'suspend': { field_id: 'status', value: 'suspend' },
                    'deleted': { field_id: 'status', value: 'deleted' },
                };

                // Split by comma and process each filter
                const filterValues = filterParam.split(',');
                initialFilters = {};

                filterValues.forEach(filterKey => {
                    const mapping = filterMapping[filterKey.trim()];
                    if (mapping) {
                        if (!initialFilters[mapping.field_id]) {
                            initialFilters[mapping.field_id] = [];
                        }
                        initialFilters[mapping.field_id].push(mapping.value);
                    }
                });

                // If no valid filters found, set to null
                if (Object.keys(initialFilters).length === 0) {
                    initialFilters = null;
                }
            }

            dispatch({type: 'init', payload: org_user, initialFilters})
        }
    }, [org_user, refresh, is_org_site_fetched, searchParams])

    return (
        <>
            <div className="content content-page">
                <TableComponent
                    name="Account list"
                    table_icon={ic_content_contact_svg}
                    table_head_fields={table_head_fields}
                    table_data={state}
                    state={state}
                    total_record={total_users}
                    table_ui={table_ui}
                    row_component={AccountListTableRowComponent}
                    doRefresh={setRefresh}
                    search={true}
                    disPatcher={dispatch}
                    isOwner={canManageAccounts}
                    permissions={permissions}
                    createText={t('@Add adminstrators@')}
                    data_table_name={'account-table'}>
                </TableComponent>
            </div>
            {openInviteModal && <AccountListInviteModalComponent openModal={openInviteModal} closeModal={closeInviteModal} organization_id={oid} disPatcher={dispatch} doRefresh={setRefresh}/>}
            {rowData && <AccountListEditAdminModalComponent openModal={openEditModal} closeModal={closeEditModal} user_data={rowData} doRefresh={setRefresh} organization_id={oid} />}
            {openAlert &&
            <ModalAlertComponent isOpen={openAlert} onClose={setOpenAlert} onConfirmAction={async (alert)=> await dispatch({type: 'deleteAdminAccount', alert})} externalAlert={true}>
                <div className="alert-msg-inner-head">{t('@Are you sure you want to remove these accounts?@')}</div>
                <div>
                    {t('@Removed accounts will lose access to all organization features, services, devices, and cloud spaces. Any personal data, if present, will be transferred to the Organization Owners view for further handling.@')}
                </div>
            </ModalAlertComponent>}

            {openLeaveAlert &&
            <ModalAlertComponent isOpen={openLeaveAlert} onClose={onCloseLeaveAlert} onConfirmAction={async (_alert, _setButton)=> await leaveOrganization(_alert, _setButton)} externalAlert={true}>
                <div className="alert-msg-inner-head">{t('@Leave this organization?@')}</div>
                <div>{t(defaultLeaveWarning)}</div>
            </ModalAlertComponent>}

            {isSuccess && <EmailWarningDialog />}

            { state.showRevoke && <AccountListRevokeOrgUserSpaceModal isOpen={state.showRevoke} onClose={()=> dispatch({type: 'toggle_show_revoke', payload: false})} row_data={state.row_data} doRefresh={setRefresh}/> }
            { state.showPermissions && <UserRolePermissionModal isOpen={state.showPermissions} onClose={()=> dispatch({type: 'toggle_show_permissions', payload: false})} user_data={state.row_data} doRefresh={setRefresh} organization_id={oid} state={state}/>}
            { state.showSuspend && <AccountListSuspendModal isOpen={state.showSuspend} onClose={()=> dispatch({type: 'toggle_show_suspend', payload: false})} row_data={state.row_data} doRefresh={setRefresh} isSuspend={state.row_data?.status==='suspend'}/> }
            { state.showShareFolder && <AccountListManageShareAccess isOpen={state.showShareFolder} onClose={()=>dispatch({type: 'toggle_show_share', payload: false})} row_data={state.row_data} doRefresh={setRefresh} organization_id={oid}/>}
        </>
    );
};

export default AccountListIndexComponent;